import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Activity, 
  Server, 
  Database, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  RefreshCw,
  Cpu,
  HardDrive,
  Layers,
  Play,
  Pause,
  RotateCcw
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const JobDashboard = () => {
  const [systemHealth, setSystemHealth] = useState(null);
  const [workerStatus, setWorkerStatus] = useState(null);
  const [queueStats, setQueueStats] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);
  const [stuckJobs, setStuckJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [error, setError] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    setError(null);
    
    const headers = getAuthHeaders();
    console.log('📊 JobDashboard: Fetching data from:', API_BASE_URL);
    
    try {
      // Fetch each endpoint individually with error handling and timeout
      const fetchWithCatch = async (url) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
        
        try {
          console.log('📡 Fetching:', url);
          const response = await fetch(url, { 
            headers,
            signal: controller.signal 
          });
          clearTimeout(timeoutId);
          
          if (response.ok) {
            const data = await response.json();
            console.log('✅ Response from', url.split('/').pop(), ':', typeof data);
            return data;
          }
          console.warn(`❌ API call failed: ${url}`, response.status);
          return null;
        } catch (e) {
          clearTimeout(timeoutId);
          console.error(`❌ Error fetching ${url}:`, e.message);
          return null;
        }
      };

      // Fetch all data in parallel with individual error handling
      const [health, worker, queue, jobs, stuck] = await Promise.all([
        fetchWithCatch(`${API_BASE_URL}/api/internal/system-health`),
        fetchWithCatch(`${API_BASE_URL}/api/internal/worker-status`),
        fetchWithCatch(`${API_BASE_URL}/api/internal/queue-stats`),
        fetchWithCatch(`${API_BASE_URL}/api/jobs/recent?limit=20`),
        fetchWithCatch(`${API_BASE_URL}/api/internal/stuck-jobs`)
      ]);

      console.log('📊 JobDashboard: Data received', { health, worker, queue, jobs, stuck });

      if (health) setSystemHealth(health);
      if (worker) setWorkerStatus(worker);
      if (queue) setQueueStats(queue);
      if (jobs) setRecentJobs(jobs.jobs || []);
      if (stuck) setStuckJobs(stuck.stuck_jobs || []);

      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 10 seconds if enabled
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => fetchData(), 10000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchData, autoRefresh]);

  const handleRetryJob = async (jobId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/internal/retry-job/${jobId}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        fetchData(true);
      }
    } catch (error) {
      console.error('Failed to retry job:', error);
    }
  };

  const handleMarkForReview = async (jobId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/internal/mark-for-review/${jobId}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        fetchData(true);
      }
    } catch (error) {
      console.error('Failed to mark job for review:', error);
    }
  };

  // Check if a job is stuck (processing for more than 30 minutes)
  const isJobStuck = (job) => {
    if (job.status !== 'processing' && job.status !== 'running') return false;
    if (!job.created_at) return false;
    
    const createdAt = new Date(job.created_at);
    const now = new Date();
    const minutesElapsed = (now - createdAt) / (1000 * 60);
    
    return minutesElapsed > 30;
  };

  // Get effective status (including stuck detection)
  const getEffectiveStatus = (job) => {
    if (isJobStuck(job)) return 'stuck';
    return job.status;
  };

  // Format current step text (business-friendly)
  const formatCurrentStep = (step) => {
    if (!step) return '-';
    // Replace "Processing" with "Extracting" for business-friendly language
    return step
      .replace(/Processing/gi, 'Extracting')
      .replace(/processed/gi, 'extracted');
  };

  const getStatusColor = (status) => {
    if (status === 'healthy' || status?.startsWith('healthy')) return 'bg-green-500';
    if (status === 'degraded') return 'bg-yellow-500';
    if (status?.includes('unhealthy')) return 'bg-red-500';
    return 'bg-gray-500';
  };

  const getStatusIcon = (status) => {
    if (status === 'healthy' || status?.startsWith('healthy')) return <CheckCircle2 className="h-5 w-5 text-green-500" />;
    if (status === 'degraded') return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    if (status?.includes('unhealthy')) return <XCircle className="h-5 w-5 text-red-500" />;
    return <Clock className="h-5 w-5 text-gray-500" />;
  };

  const getJobStatusBadge = (status) => {
    const colors = {
      completed: 'bg-green-100 text-green-800 border-green-200',
      running: 'bg-blue-100 text-blue-800 border-blue-200',
      processing: 'bg-blue-100 text-blue-800 border-blue-200',
      queued: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      failed: 'bg-red-100 text-red-800 border-red-200',
      stuck: 'bg-orange-100 text-orange-800 border-orange-200',
      review: 'bg-purple-100 text-purple-800 border-purple-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  if (loading && !systemHealth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
          <p className="text-gray-400 text-xs mt-2">Connecting to {API_BASE_URL || 'backend'}</p>
          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Job Processing Dashboard</h1>
            <p className="text-gray-600 text-sm">
              Monitor Celery workers, Redis queues, and extraction jobs
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-500">
              {lastRefresh && `Last updated: ${lastRefresh.toLocaleTimeString()}`}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={autoRefresh ? 'bg-green-50' : ''}
            >
              {autoRefresh ? <Pause className="h-4 w-4 mr-1" /> : <Play className="h-4 w-4 mr-1" />}
              {autoRefresh ? 'Auto' : 'Paused'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchData(true)}
              disabled={refreshing}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* System Health Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {/* Overall Status */}
          <Card className={`border-l-4 ${systemHealth?.overall === 'healthy' ? 'border-l-green-500' : systemHealth?.overall === 'degraded' ? 'border-l-yellow-500' : 'border-l-red-500'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Overall Status</p>
                  <p className="text-lg font-semibold capitalize">{systemHealth?.overall || 'Unknown'}</p>
                </div>
                <Activity className={`h-8 w-8 ${systemHealth?.overall === 'healthy' ? 'text-green-500' : 'text-red-500'}`} />
              </div>
            </CardContent>
          </Card>

          {/* API Server */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">API Server</p>
                  <p className="text-lg font-semibold capitalize">{systemHealth?.api_server || 'Unknown'}</p>
                </div>
                {getStatusIcon(systemHealth?.api_server)}
              </div>
            </CardContent>
          </Card>

          {/* Database */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Database</p>
                  <p className="text-lg font-semibold capitalize">{systemHealth?.database || 'Unknown'}</p>
                </div>
                <Database className={`h-6 w-6 ${systemHealth?.database === 'healthy' ? 'text-green-500' : 'text-red-500'}`} />
              </div>
            </CardContent>
          </Card>

          {/* Redis */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Redis Queue</p>
                  <p className="text-lg font-semibold capitalize">
                    {systemHealth?.redis?.startsWith('healthy') ? 'Healthy' : systemHealth?.redis?.includes('unhealthy') ? 'Down' : 'Unknown'}
                  </p>
                </div>
                <HardDrive className={`h-6 w-6 ${systemHealth?.redis?.startsWith('healthy') ? 'text-green-500' : 'text-red-500'}`} />
              </div>
            </CardContent>
          </Card>

          {/* Celery Workers */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Celery Workers</p>
                  <p className="text-lg font-semibold">
                    {systemHealth?.celery_workers?.match(/\d+/)?.[0] || '0'} Active
                  </p>
                </div>
                <Cpu className={`h-6 w-6 ${systemHealth?.celery_workers?.startsWith('healthy') ? 'text-green-500' : 'text-red-500'}`} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Queue Stats & Worker Info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Queue Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layers className="h-5 w-5" />
                Queue Statistics
              </CardTitle>
              <CardDescription>Current jobs in Redis queues</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Extraction Queue</span>
                  <Badge variant="outline" className="text-lg px-3">
                    {queueStats?.queues?.extraction || 0}
                  </Badge>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Default Queue</span>
                  <Badge variant="outline" className="text-lg px-3">
                    {queueStats?.queues?.default || 0}
                  </Badge>
                </div>
                {queueStats?.redis_info && (
                  <>
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                      <span className="text-gray-600">Redis Clients</span>
                      <span className="font-medium">{queueStats.redis_info.connected_clients}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                      <span className="text-gray-600">Redis Memory</span>
                      <span className="font-medium">{queueStats.redis_info.used_memory_human}</span>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Worker Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="h-5 w-5" />
                Worker Details
              </CardTitle>
              <CardDescription>Registered Celery tasks and workers</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {workerStatus?.registered_tasks && Object.entries(workerStatus.registered_tasks).map(([worker, tasks]) => (
                  <div key={worker} className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-2 truncate" title={worker}>
                      {worker.split('@')[1]?.substring(0, 20) || worker.substring(0, 20)}...
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {tasks.slice(0, 4).map((task, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {task.split('.').pop()}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
                {(!workerStatus?.registered_tasks || Object.keys(workerStatus.registered_tasks).length === 0) && (
                  <div className="text-center py-4 text-gray-500">
                    <XCircle className="h-8 w-8 mx-auto mb-2 text-red-400" />
                    <p>No workers connected</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Stuck Jobs Alert */}
        {stuckJobs.length > 0 && (
          <Card className="mb-6 border-orange-200 bg-orange-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-700">
                <AlertCircle className="h-5 w-5" />
                Stuck Jobs ({stuckJobs.length})
              </CardTitle>
              <CardDescription className="text-orange-600">
                These jobs have been running for more than 30 minutes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {stuckJobs.map((job) => (
                  <div key={job.job_id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-orange-200">
                    <div>
                      <p className="font-mono text-sm">{job.job_id?.substring(0, 8)}...</p>
                      <p className="text-xs text-gray-500">{job.workflow_type} - {job.current_step}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRetryJob(job.job_id)}
                      className="text-orange-600 border-orange-300 hover:bg-orange-100"
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Retry
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Jobs Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Recent Extraction Jobs
            </CardTitle>
            <CardDescription>Last 20 jobs across all workflows</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Job ID</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Workflow</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Progress</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Current Step</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Created</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentJobs.map((job) => {
                    const effectiveStatus = getEffectiveStatus(job);
                    const isStuck = effectiveStatus === 'stuck';
                    const canRetry = effectiveStatus === 'failed' || isStuck;
                    const canMarkReview = effectiveStatus === 'stuck' || effectiveStatus === 'failed';
                    
                    return (
                      <tr key={job.job_id} className={`border-b hover:bg-gray-50 ${isStuck ? 'bg-orange-50' : ''}`}>
                        <td className="py-3 px-4 font-mono text-xs">
                          {job.job_id?.substring(0, 8)}...
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className="uppercase text-xs">
                            {job.workflow_type || 'unknown'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Badge 
                            className={getJobStatusBadge(effectiveStatus)}
                            title={isStuck ? 'Running longer than 30 minutes' : ''}
                          >
                            {effectiveStatus}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${
                                  effectiveStatus === 'completed' ? 'bg-green-500' : 
                                  effectiveStatus === 'failed' ? 'bg-red-500' : 
                                  isStuck ? 'bg-orange-500' : 'bg-blue-500'
                                }`}
                                style={{ width: `${job.progress_percent || 0}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-500">{job.progress_percent || 0}%</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-xs text-gray-600 max-w-xs truncate" title={job.current_step}>
                          {formatCurrentStep(job.current_step)}
                        </td>
                        <td className="py-3 px-4 text-xs text-gray-500">
                          {job.created_at ? new Date(job.created_at).toLocaleString() : '-'}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-1">
                            {canRetry && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRetryJob(job.job_id)}
                                className="h-7 px-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                title="Retry this job"
                              >
                                <RotateCcw className="h-3 w-3" />
                              </Button>
                            )}
                            {canMarkReview && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleMarkForReview(job.job_id)}
                                className="h-7 px-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50"
                                title="Mark for manual review"
                              >
                                <AlertCircle className="h-3 w-3" />
                              </Button>
                            )}
                            {!canRetry && !canMarkReview && (
                              <span className="text-gray-300 text-xs">-</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {recentJobs.length === 0 && (
                    <tr>
                      <td colSpan="7" className="py-8 text-center text-gray-500">
                        No jobs found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default JobDashboard;
